import {PolymerElement, html} from '@polymer/polymer';
import {ScopeBinding} from './behaviors/keli-binding-behavior.js';
import {ActionBehavior} from './behaviors/action-behavior.js';
import {renderKeliMarkdown} from '../javascripts/modules/renderKeliMarkdown.js';
import {backgroundColorValue, desiredTextColor, foregroundColorValue} from '../javascripts/modules/colors.js';
import {TooltipBehavior} from './behaviors/tooltip-behavior.js';

class KeliValue extends ScopeBinding(ActionBehavior(TooltipBehavior(PolymerElement))) {
  static get template() {
    return html`
<style>
  :host {
    display: inline-block;
    position: relative;
    font-size: var(--keli-value-font-size, 1em);
    padding: var(--keli-value-padding, 0 0 10px 0);
    min-height: var(--keli-value-min-height, 35px);
    width: var(--keli-value-width, auto);
    background-color: var(--keli-value-background-color);
  }

  :host(.no-label) #label {
    display: none !important;
  }

  :host(.no-style) {
    padding: 0;
    min-height: auto;
  }

  :host(.has-hotkey) {
    padding-bottom: 0;
  }

  :host(.has-icon) #fieldIcon {
    display: block;
  }

  .hidden {
    display: none;
    visibility: hidden;
  }

  #label {
    display: block;
    width: 100%;
    overflow: hidden;
    font-size: .7em;
    line-height: 1em;
    font-weight: 400;
    cursor: default;
    white-space: nowrap;
    color: var(--keli-field-label-color);
  }

  #label.help-icon-visible {
      width: calc(100% - 14px);
  }

  #textContainer {
    box-sizing: border-box;
    min-height: 17px;
    margin: var(--keli-value-text-margin, 0);
    text-align: var(--keli-value-text-align, start);
  }

  #textContainer.has-icon {
    padding-right: 20px;
    min-height: 1em;
  }

  #textContainer.has-action #text {
      text-decoration: underline;
      cursor: pointer;
      color: var(--keli-a-color);
  }

  :host(.has-hotkey) #textContainer {
    min-height: auto;
  }
  
  #text {
    color: var(--keli-value-text-color, var(--keli-text));
    font-size: .93em;
    font-weight: 400;
  }
  
  #hotkey {
    color: var(--keli-text-subtle);
    font-size: .6em;
    display: block;
  }

  p {
    margin: 0 0 1em 0;
    padding: 0;
  }

  p:last-child {
    margin: 0;
  }

  #fieldIcon {
    position: absolute;
    right: 0;
    top: .65em;
    cursor: pointer;
    width: 20px;
    height: 20px;
    text-align: right;
    color: var(--keli-field-icon-color);
  }

  #fieldIcon:hover {
    color: var(--keli-field-icon-hover-color);
  }

  sl-icon.help, sl-icon.fieldIcon {
    font-size: 16px;
  }

  #help {
      position: absolute;
      right: 0;
      font-size: .7em;
      top: 0;
      color: var(--keli-text-subtle);
      cursor: help;
  }

  #help:hover {
      color: var(--keli-text);
  }
</style>

<span id="label" class$="[[computeLabelClass(caption, helpText, chrome, _initialized)]]">[[caption]]</span>
<sl-icon id="help" name="help-circle-outline" class$="[[computeHelpClass(helpText, chrome, _initialized)]]"></sl-icon>
<sl-icon id="fieldIcon" name="[[formattedIcon]]" on-click="handleIconClick" class$="[[computeFieldIconClass(formattedIcon, _initialized)]]"></sl-icon>
<div id="textContainer" class$="[[computeContainerClass(formattedIcon, hasAction, _initialized)]]">
    <span id="text" on-click="handleClick">[[displayValue]]</span>
</div>
<span id="hotkey" class$="[[computeHotkeyClass(formattedShortcutKey, chrome, _initialized)]]">[[hotkey]]</span>
`;
  }

  static get is() {
    return 'keli-value';
  }

  static get properties() {
    return {
      _initialized: {type: Boolean},
      align: {type: String, scopevar: true, observer: 'alignChanged'},
      backgroundColor: {type: String, observer: 'backgroundColorChanged', scopevar: true},
      caption: {type: String, scopevar: true},
      chrome: {type: Boolean, value: true},
      color: {type: String, scopevar: true, observer: 'colorChanged'},
      displayValue: {type: String, scopevar: {options: {peek: false}}, observer: 'displayValueChanged'},
      fontScale: {type: String, scopevar: true, observer: 'fontScaleChanged'},
      formattedIcon: {type: String, computed: 'computeFormattedIcon(icon)'},
      formattedShortcutKey: {type: String, computed: 'computeFormattedShortcutKey(hasAction, shortcutKey)'},
      hasAction: {type: String, scopevar: true},
      helpText: {type: String, scopevar: 'help', observer: 'helpTextChanged'},
      hotkey: {type: String, scopevar: true, observer: 'hotkeyChanged'},
      icon: {type: String, scopevar: true},
      keliControlTypes: {type: Array, value: ['display']},
      shortcutKey: {type: String}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli('keliIconAction');
  }

  connectedCallback() {
    super.connectedCallback();
    this._initialized = true;
  }

  computeHelpClass(helpText, chrome) {
    const classList = [];
    if (!helpText || !chrome) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  computeLabelClass(caption, helpText, chrome) {
    const classList = [];
    if (!caption || !chrome) {
      classList.push('hidden');
    }
    if (helpText) {
      classList.push('help-icon-visible');
    }
    return classList.join(' ');
  }

  computeFieldIconClass(formattedIcon) {
    const classList = [];
    if (!formattedIcon) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  computeContainerClass(formattedIcon, hasAction) {
    const classList = [];
    if (formattedIcon) {
      classList.push('has-icon');
    }
    if (hasAction) {
      classList.push('has-action');
    }
    return classList.join(' ');
  }

  computeHotkeyClass(formattedShortcutKey, chrome) {
    const classList = [];
    if (!formattedShortcutKey || !chrome) {
      classList.push('hidden');
    }
    return classList.join(' ');
  }

  activateShortcutKey(event) {
    if (this.hasAction) {
      this._activate(event);
    }
  }

  handleClick(event) {
    if (this.hasAction) {
      this._activate(event);
    }
  }

  handleIconClick(event) {
    event.stopPropagation();
    this.dispatchEvent(new CustomEvent('keliIconAction'));
  }

  computeFormattedIcon(icon) {
    return KeliInterface.Styles.parseIconName(icon);
  }

  computeFormattedShortcutKey(hasAction, shortcutKey) {
    if (KeliInterface.isTouchDevice || !hasAction) {
      return;
    }
    return (shortcutKey ? formatHotkeyAsText(shortcutKey) : undefined);
  }

  alignChanged(newValue) {
    this.updateStyles({'--keli-value-text-align': newValue || ''});
  }

  backgroundColorChanged(newValue) {
    const backgroundColor = backgroundColorValue(newValue);
    const foregroundColor = (backgroundColor ? desiredTextColor(backgroundColor) : '');
    this.updateStyles({
      '--keli-value-background-color': backgroundColor || '',
      '--keli-value-text-color': foregroundColor,
      '--keli-field-label-color': foregroundColor
    });
  }

  colorChanged(newValue) {
    const color = foregroundColorValue(newValue, this);
    this.updateStyles({'--keli-value-text-color': color || ''});
  }

  fontScaleChanged(newValue) {
    const size = parseFloat(newValue);
    this.updateStyles({'--keli-value-font-size': (!isNaN(size) ? `${size}em` : '')});
  }

  helpTextChanged(newValue) {
    const tooltip = this.getTooltipInstance(this.$.help);
    if (tooltip) {
      if (newValue) {
        tooltip.enable();
      } else {
        tooltip.disable();
      }
    } else if (newValue) {
      this.addTooltip(this.$.help, 'helpText', {
        triggerTarget: this.$.help,
        placement: 'top-end',
        offset: [0, 0]
      });
    }
  }

  hotkeyChanged(newValue, oldValue) {
    this.classList.toggle('has-hotkey', !!newValue);

    if (newValue === true || KeliInterface.Hotkeys.alphaNumerics.includes(newValue)) {
      this.dispatchEvent(new CustomEvent('panelElementAttributeChanged', {
        bubbles: true, composed: true,
        detail: {element: this, attribute: 'hotkey'}
      }));
    } else {
      this.shortcutKey = newValue;
      if (newValue) {
        this.dispatchEvent(new CustomEvent('registerPanelHotkey', {
          bubbles: true, composed: true,
          detail: {
            'keys': newValue, 'scope': this, action: event => {
              this.activateShortcutKey(event);
            }
          }
        }));
      }
      if (oldValue) {
        this.dispatchEvent(new CustomEvent('unregisterPanelHotkey', {
          bubbles: true, composed: true,
          detail: {'keys': oldValue}
        }));
      }
    }
  }

  displayValueChanged(newValue) {
    renderKeliMarkdown(newValue || '', this.$.text, {preventSolitaryParagraph: true});
  }
}

customElements.define(KeliValue.is, KeliValue);
export {KeliValue};
